// Componenti condivisi per la stampa/anteprima dei modelli Privacy.
// Generici rispetto all'ambito (trattamento / docente / esterno / nomina).
//
// Props comuni:
//  - ambito: "trattamento" | "docente" | "esterno" | "nomina"
//  - paramName: nome del query param del backend ("trattamento_codice" | "docente_codice" | ...)
//  - paramValue: valore del codice entità da stampare
//  - titleSuffix: stringa visualizzata nel titolo del modal (es. codice + nome docente)
//  - onClose: callback chiusura
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { DataStampaField, oggiDmy } from "./DataStampaField";
import { LibreOfficeInstallButton } from "./LibreOfficeInstallButton";

// =====================================================================
// STAMPA MODAL — scegli modello + data, scarica DOCX o PDF
// =====================================================================
export function StampaModelloModal({ ambito, paramName, paramValue, titleSuffix, onClose, testidPrefix = "priv-stampa" }) {
  const [modelli, setModelli] = useState([]);
  const [sel, setSel] = useState("");
  const [dataStampa, setDataStampa] = useState(oggiDmy());
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/privacy/modelli", { params: { ambito } });
        if (!alive) return;
        const list = r.data?.items || [];
        setModelli(list);
        if (list.length > 0) setSel(list[0].codice);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [ambito]);

  const scarica = async (formato) => {
    setErr("");
    try {
      const r = await api.get(`/privacy/modelli/${encodeURIComponent(sel)}/genera`, {
        params: { [paramName]: paramValue, formato, data_stampa: dataStampa },
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: r.headers["content-type"] });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `${paramValue}.${formato}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      onClose?.();
    } catch (e) {
      let msg = "Errore generazione documento";
      try {
        const txt = await e.response?.data?.text?.();
        if (txt) { const j = JSON.parse(txt); msg = j.detail || msg; }
      } catch { /* ignore */ }
      setErr(msg);
    }
  };

  return (
    <div onClick={onClose} data-testid={`${testidPrefix}-modal-bg`}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} data-testid={`${testidPrefix}-modal`}
        style={{ background: "white", padding: 20, minWidth: 480, maxWidth: 600, boxShadow: "0 6px 30px rgba(0,0,0,0.3)" }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: "#0d47a1" }}>
          🖨 Stampa con modello{titleSuffix ? ` — ${titleSuffix}` : ""}
        </div>
        {err && (
          <div style={{ padding: 8, background: "#fff0f0", color: "#c00", fontSize: 11, marginBottom: 10 }}>
            {err} <button onClick={() => setErr("")} style={{ float: "right", background: "none", border: "none", cursor: "pointer", color: "#c00", fontWeight: 700 }}>✕</button>
          </div>
        )}
        <DataStampaField value={dataStampa} onChange={setDataStampa} testid={`${testidPrefix}-data`} />
        <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>MODELLO</div>
        <select value={sel} onChange={e => setSel(e.target.value)} data-testid={`${testidPrefix}-modello-select`}
          style={{ width: "100%", padding: "6px 10px", border: "1px solid #ccc", fontSize: 12, marginBottom: 16 }}>
          {modelli.length === 0 && <option value="">— nessun modello disponibile per ambito "{ambito}" —</option>}
          {modelli.map(m => (
            <option key={m.codice} value={m.codice}>
              {m.codice} — {m.nome} {m.filename ? `(${m.filename})` : "(nessun file)"}
            </option>
          ))}
        </select>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} data-testid={`${testidPrefix}-cancel`}
            style={{ padding: "8px 16px", background: "white", border: "1px solid #999", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            Annulla
          </button>
          <button onClick={() => scarica("docx")} disabled={!sel} data-testid={`${testidPrefix}-docx`}
            style={{ padding: "8px 16px", background: "#1565c0", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: sel ? "pointer" : "not-allowed" }}>
            📄 Scarica DOCX
          </button>
          <button onClick={() => scarica("pdf")} disabled={!sel} data-testid={`${testidPrefix}-pdf`}
            style={{ padding: "8px 16px", background: "#c62828", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: sel ? "pointer" : "not-allowed" }}>
            📕 Scarica PDF
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// ANTEPRIMA MODAL — scegli modello e mostra PDF in iframe a tutto schermo
// =====================================================================
export function AnteprimaModelloModal({ ambito, paramName, paramValue, titleSuffix, onClose, testidPrefix = "priv-anteprima" }) {
  const [modelli, setModelli] = useState([]);
  const [sel, setSel] = useState("");
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [err, setErr] = useState("");
  const [loMissing, setLoMissing] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [dataStampa, setDataStampa] = useState(oggiDmy());

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/privacy/modelli", { params: { ambito } });
        if (alive) setModelli(r.data?.items || []);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [ambito]);

  useEffect(() => {
    if (!sel) return;
    let alive = true;
    let createdUrl = "";
    (async () => {
      setLoading(true); setErr(""); setLoMissing(false);
      try {
        const r = await api.get(`/privacy/modelli/${encodeURIComponent(sel)}/genera`, {
          params: { [paramName]: paramValue, formato: "pdf", data_stampa: dataStampa },
          responseType: "blob",
        });
        if (!alive) return;
        const blob = new Blob([r.data], { type: "application/pdf" });
        createdUrl = URL.createObjectURL(blob);
        setPdfUrl(createdUrl);
      } catch (e) {
        if (!alive) return;
        let msg = "Errore generazione anteprima";
        try {
          const txt = await e.response?.data?.text?.();
          if (txt) { const j = JSON.parse(txt); msg = j.detail || msg; }
        } catch { /* ignore */ }
        if (e.response?.status === 503 || /LibreOffice/i.test(msg)) setLoMissing(true);
        setErr(msg);
        setPdfUrl("");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; if (createdUrl) URL.revokeObjectURL(createdUrl); };
  }, [sel, paramName, paramValue, dataStampa, reloadTick]);

  const scarica = async (formato) => {
    try {
      const r = await api.get(`/privacy/modelli/${encodeURIComponent(sel)}/genera`, {
        params: { [paramName]: paramValue, formato, data_stampa: dataStampa },
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: r.headers["content-type"] });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `${paramValue}.${formato}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      setErr("Errore download");
    }
  };

  return (
    <div onClick={onClose} data-testid={`${testidPrefix}-bg`}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} data-testid={`${testidPrefix}-modal`}
        style={{ background: "white", width: "90vw", maxWidth: 1200, height: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 6px 30px rgba(0,0,0,0.4)" }}>
        <div style={{ padding: "12px 16px", background: "#6a1b9a", color: "white", display: "flex", alignItems: "center", gap: 12, justifyContent: "space-between" }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>👁 Anteprima{titleSuffix ? ` — ${titleSuffix}` : ""}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, opacity: 0.9 }} title="Data di stampa (placeholder «Data.Stampa»)">📅 Data:</span>
            <input type="text" value={dataStampa} onChange={e => setDataStampa(e.target.value)}
              placeholder="gg/mm/aaaa"
              data-testid={`${testidPrefix}-data-stampa`}
              title="Data di stampa (placeholder «Data.Stampa»)"
              style={{ padding: "4px 8px", border: "1px solid #ccc", fontSize: 12, width: 100, color: "#37474f", background: "white" }} />
            <select value={sel} onChange={e => setSel(e.target.value)} data-testid={`${testidPrefix}-modello-select`}
              style={{ padding: "4px 8px", border: "1px solid #ccc", fontSize: 12, minWidth: 280, color: "#37474f", background: "white" }}>
              <option value="">— scegli un modello —</option>
              {modelli.map(m => (
                <option key={m.codice} value={m.codice}>{m.codice} — {m.nome}{m.filename ? "" : " (no file)"}</option>
              ))}
            </select>
            <button onClick={() => scarica("docx")} disabled={!sel || loading} data-testid={`${testidPrefix}-docx`}
              style={{ padding: "5px 12px", background: "#1565c0", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: !sel || loading ? "not-allowed" : "pointer", opacity: !sel || loading ? 0.5 : 1 }}>📄 DOCX</button>
            <button onClick={() => scarica("pdf")} disabled={!sel || loading} data-testid={`${testidPrefix}-pdf`}
              style={{ padding: "5px 12px", background: "#c62828", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: !sel || loading ? "not-allowed" : "pointer", opacity: !sel || loading ? 0.5 : 1 }}>📕 PDF</button>
            <button onClick={onClose} data-testid={`${testidPrefix}-close`}
              style={{ background: "none", border: "none", color: "white", fontSize: 22, cursor: "pointer", padding: "0 4px" }}>✕</button>
          </div>
        </div>

        <div style={{ flex: 1, background: "#37474f", position: "relative", overflow: "hidden" }}>
          {loading && (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 13, fontWeight: 700 }}>
              ⏳ Generazione anteprima in corso...
            </div>
          )}
          {!loading && err && (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 10, padding: 20 }}>
              <div style={{ background: "#c62828", color: "white", padding: "12px 18px", fontSize: 12, fontWeight: 700, maxWidth: 600, textAlign: "center" }}>{err}</div>
              {loMissing && (
                <div style={{ background: "white", padding: "12px 18px", borderRadius: 4, textAlign: "center" }}>
                  <LibreOfficeInstallButton onReady={() => { setLoMissing(false); setErr(""); setReloadTick(t => t + 1); }} />
                </div>
              )}
            </div>
          )}
          {!loading && !err && pdfUrl && (
            <iframe src={pdfUrl} title="Anteprima PDF" data-testid={`${testidPrefix}-iframe`}
              style={{ width: "100%", height: "100%", border: "none", background: "white" }} />
          )}
          {!loading && !err && !pdfUrl && !sel && (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12, color: "#cfd8dc", fontSize: 14 }}>
              <div style={{ fontSize: 48 }}>📄</div>
              <div style={{ fontWeight: 700 }}>Scegli un modello dall'alto per generare l'anteprima</div>
              {modelli.length === 0 && (
                <div style={{ fontSize: 12, color: "#ff8a80" }}>Nessun modello disponibile per ambito "{ambito}". Caricane uno da Privacy → Catalogo Docx.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
